import { memo, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import { Icon, Tag, Text, VerticalNav } from '@zillow/constellation-10';

import AdaptiveAnchor from '../AdaptiveAnchor';

import HeaderNavigationClickstreamEvents from '../../utils/clickstream';

import type React from 'react';

export type Props = {
  target?: string;
  title?: string;
  path?: string | null;
  escape?: boolean;
  isActive?: boolean;
  isMobileWeb?: boolean;
  useAbsolutePaths?: boolean;
  newTagCookieName?: string;
  showNewTag?: boolean;
  // Link component from react-router or next.js
  link?: React.ComponentType<React.PropsWithChildren<{ to: string }>>;

  ActiveIcon?: React.ReactNode;
  InactiveIcon?: React.ReactNode;
  TrailingIcon?: React.ReactNode;

  onClick?: (event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => void | null;
};

const NavigationItem = ({
  title,
  target = '_self',
  path = null,
  escape = false,
  isActive = false,
  isMobileWeb = false,
  useAbsolutePaths,
  link,
  newTagCookieName,
  showNewTag = true,
  ActiveIcon,
  InactiveIcon,
  TrailingIcon,
  onClick,
}: Props) => {
  // Initialize state to false (hidden) to prevent flash for returning users
  const [shouldShowNewTag, setShouldShowNewTag] = useState(false);

  // When showNewTag is true: show tag only if cookie not set.
  // When false (e.g. canViewOnboardingFlow true): set cookie so tag won't reappear if flag later flips.
  useEffect(() => {
    if (newTagCookieName) {
      if (showNewTag && Cookies.get(newTagCookieName) !== 'true') {
        setShouldShowNewTag(true);
      } else {
        if (!showNewTag) {
          Cookies.set(newTagCookieName, 'true', { expires: 365 });
        }
        setShouldShowNewTag(false);
      }
    }
  }, [showNewTag, newTagCookieName]);

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    // Set cookie and state to hide tag after first click
    if (shouldShowNewTag && newTagCookieName) {
      Cookies.set(newTagCookieName, 'true', { expires: 365 }); // 1 year
      setShouldShowNewTag(false);
    }

    if (onClick) {
      onClick(event);
    }
  };

  return (
    <VerticalNav.Item
      selected={isActive && !isMobileWeb}
      data-testid={isActive ? 'NavigationItem-Active' : 'NavigationItem'}
    >
      <AdaptiveAnchor
        data-testid="NavigationItem"
        target={target}
        title={title}
        escape={escape}
        link={link}
        path={path}
        useAbsolutePaths={useAbsolutePaths}
        onClick={handleClick}
        data-eventid={HeaderNavigationClickstreamEvents.ClickNavigationLink.data_event_id}
      >
        {Boolean(ActiveIcon || InactiveIcon) && (
          <Icon css={{ marginRight: '200' }} size="md">
            {isActive ? ActiveIcon : InactiveIcon}
          </Icon>
        )}
        <Text css={{ textStyle: isActive && !isMobileWeb ? 'body-bold' : 'body' }}>{title}</Text>
        {shouldShowNewTag && (
          <Tag css={{ marginLeft: 'auto' }} size="sm" tone="purple">
            New
          </Tag>
        )}
        {/* Icon slots TrailingIcon, merging its 1em size onto it; wrap a non-icon element that sets its own size */}
        {Boolean(TrailingIcon) && (
          // oxlint-disable-next-line constellation/no-raw-spacing-values
          <Icon css={{ position: 'relative', top: '1px', marginLeft: 'auto', lineHeight: 0 }}>{TrailingIcon}</Icon>
        )}
      </AdaptiveAnchor>
    </VerticalNav.Item>
  );
};

export default memo(NavigationItem);
